import { z } from 'zod';

/** A locadora como o catálogo do rental-car-api a conhece (`rentalProviders`):
 *  o código (o mesmo `rental.id` das ofertas, `MO`, `LL`…) e o nome. */
const RentalPreferredCompanySchema = z
  .object({
    code: z.string().min(1).max(40),
    name: z.string().trim().min(1).max(80),
  })
  .strict();

/** O lugar de retirada como a busca de carro o conhece: o ref opaco que ela
 *  re-hidrata (`google:<placeId>` | `airport:GRU`) e o rótulo que a pessoa
 *  escolheu — o mesmo par das buscas recentes de carro. */
const RentalPreferredPlaceSchema = z
  .object({
    ref: z.string().min(1).max(300),
    label: z.string().trim().min(1).max(200),
  })
  .strict();

/** Automático ou manual — o câmbio do carro. */
const RentalTransmissionSchema = z.enum(['automatic', 'manual']);

/**
 * As preferências de ALUGUEL DE CARRO do viajante — declaradas por ele na aba
 * "Aluguel de carro" das Preferências, no molde das de ônibus
 * (`BusPreferences`). O legado não tem o campo, então ele vive num store do
 * customer-api (`rental_preferences`) — a exceção sancionada em "Profile
 * truth". `null` = sem preferência.
 *
 * - `company`: a locadora do catálogo do rental-car-api (código + nome).
 * - `category`: o CÓDIGO da categoria do catálogo (`rentalCategories`:
 *   `small`, `medium`, `suv`…). Só o código: o nome vem traduzido pelo
 *   rental-car-api no idioma de quem pede, e congelá-lo prenderia um idioma. O
 *   catálogo o devolve em minúsculas e a oferta em maiúsculas (`SMALL`):
 *   compare sem caixa.
 * - `transmission`: o câmbio.
 * - `pickupPlace`: o lugar de retirada de costume.
 *
 * Wire de leitura E de escrita (`PUT` substitui o documento inteiro).
 */
export const RentalPreferencesSchema = z
  .object({
    company: RentalPreferredCompanySchema.nullable(),
    category: z.string().trim().min(1).max(40).nullable(),
    transmission: RentalTransmissionSchema.nullable(),
    pickupPlace: RentalPreferredPlaceSchema.nullable(),
  })
  .strict();
export type RentalPreferences = z.infer<typeof RentalPreferencesSchema>;

/** Quem nunca salvou nada: nenhuma preferência. */
export const DEFAULT_RENTAL_PREFERENCES: RentalPreferences = {
  company: null,
  category: null,
  transmission: null,
  pickupPlace: null,
};
